<template>
	<HorizontalSplit>
		<template #top>
			<div class="map-wrap">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="initialCenter"
					:zoom="11"
					@map:load="onMapLoad"
				>
					<SourceTegola id="review-site-dots" map-name="nidus">
						<mgl-circle-layer
							layer-id="review-site-dots"
							source-layer="site"
							:paint="sitePaint"
						/>
						<mgl-circle-layer
							v-if="selectedSiteID !== 0"
							layer-id="review-site-dots-selected"
							source-layer="site"
							:paint="selectedPaint"
							:filter="selectedFilter"
						/>
					</SourceTegola>
				</mgl-map>
			</div>
		</template>
		<template #bottom>
			<div class="bottom-pane">
				<div class="toolbar d-flex gap-2 align-items-center p-2 border-bottom">
					<div class="input-group" style="max-width: 360px">
						<span class="input-group-text"><i class="bi bi-search"></i></span>
						<input
							type="text"
							class="form-control"
							placeholder="Search by address…"
							v-model="search"
						/>
					</div>
					<small class="text-muted ms-auto"
						>{{ sites.length }} / {{ collectionTotal }} sites</small
					>
					<template v-if="selectedSite">
						<template v-if="session.organization?.lob_address_id">
							<ButtonLoading
								@click="doRequestComplianceMailer(selectedSite.id)"
								:disabled="!selectedSite"
								icon="bi-check-circle"
								:loading="submitting"
								text="Send Compliance Mailer"
								variant="success"
							/>
						</template>
						<RouterLink
							v-else
							:to="routes.SiteDetail(selectedSite.id)"
							class="btn btn-outline-secondary btn-sm"
						>
							Set Lob Address ID
						</RouterLink>
					</template>
				</div>
				<div class="table-scroll">
					<table class="table table-hover mb-0">
						<thead>
							<tr>
								<th style="width: 80px">ID</th>
								<th>Address</th>
								<th>Owner</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="site in sites"
								:key="site.id"
								:class="{ 'table-primary': selectedSiteID === site.id }"
								@click="siteSelect(site)"
								style="cursor: pointer"
							>
								<td>{{ site.id }}</td>
								<td>{{ site.address }}</td>
								<td>{{ site.owner_name || "—" }}</td>
								<td class="text-end">
									<RouterLink
										:to="routes.SiteDetail(site.id)"
										class="btn btn-sm btn-outline-primary"
										@click.stop
									>
										<i class="bi bi-box-arrow-up-right"></i> Details
									</RouterLink>
								</td>
							</tr>
							<tr v-if="sites.length === 0">
								<td colspan="4" class="text-center text-muted py-3">
									No sites match
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</HorizontalSplit>
</template>

<style scoped lang="scss">
.map-wrap {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.bottom-pane {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.table-scroll {
	flex: 1;
	overflow-y: auto;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { MglCircleLayer, MglMap, useMap, type MglEvent } from "vue-maplibre-gl";
import type { MapLayerMouseEvent } from "maplibre-gl";

import { useQueryParam } from "@/composable/use-query-param";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import SourceTegola from "@/map/SourceTegola.vue";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsForServiceArea, mapStyleURL } from "@/map/util";
import { useStoreSite } from "@/store/site";
import { useSessionStore } from "@/store/session";
import { useRoutes } from "@/route/use";
import type { MapReviewQueryBodyFilter, MapReviewSiteRow } from "@/type/api";

const session = useSessionStore();
const storeSite = useStoreSite();
const routes = useRoutes();
const mapInstance = useMap();

const error = ref<string>("");
const search = ref("");
const selectedSiteID = ref<number>(0);
// The selected row, kept beside its id: the id drives the map highlight and the
// table's row state, while the row itself keeps the mailer action on hand when a
// search narrows the list the selection was made from.
const selectedSite = ref<MapReviewSiteRow | undefined>(undefined);
const submitting = ref<boolean>(false);
const paramSite = useQueryParam("site");

// The list is the server's own page of the Sites ledger — its address column
// already carries number, street, locality, region and postal code, and its
// owner is the joined name — so the view keeps no copy of the collection and
// narrows through the query instead of filtering one in memory.
const sites = ref<MapReviewSiteRow[]>([]);
const collectionTotal = ref<number>(0);

// The search box's own clause: one `contains` over the address the ledger row
// composes. A keystroke per query would ask the server for a narrowing per
// keystroke, so text settles first (the ledger's own debounce, map-review.ts).
const SEARCH_DEBOUNCE_MS = 250;

/** The narrowing the search box states, or nothing while it is empty. */
function searchFilter(): MapReviewQueryBodyFilter[] {
	const needle = search.value.trim();
	if (needle === "") {
		return [];
	}
	return [{ property: "address", operator: "contains", value: needle }];
}

// Start centered on the district's service area when available.
const initialCenter = computed<[number, number]>(() => {
	const sa = session.organization?.service_area;
	if (sa && sa.min) {
		return [
			(sa.min.longitude + sa.max.longitude) / 2,
			(sa.min.latitude + sa.max.latitude) / 2,
		];
	}
	return [-119.175, 36.24];
});

const sitePaint = {
	"circle-radius": 5,
	"circle-color": "#0d6efd",
	"circle-opacity": 0.9,
	"circle-stroke-color": "#ffffff",
	"circle-stroke-width": 1,
} as const;

const selectedPaint = {
	"circle-radius": 8,
	"circle-color": "#ffc107",
	"circle-opacity": 1,
	"circle-stroke-color": "#000000",
	"circle-stroke-width": 1.5,
} as const;

const selectedFilter = computed<any>(() => {
	if (selectedSiteID.value === 0) {
		return ["==", ["get", "id"], -1];
	}
	return ["==", ["get", "id"], selectedSiteID.value];
});

async function doRequestComplianceMailer(id: number) {
	submitting.value = true;
	try {
		const response = await fetch("/api/compliance-request/mailer", {
			body: JSON.stringify({ site_id: id }),
			headers: {
				"Content-Type": "application/json",
			},
			method: "POST",
		});
		if (!response.ok) {
			throw new Error("failed to create compliance request");
		}
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Unknown error";
		console.error("Error submitting review:", err);
	} finally {
		submitting.value = false;
	}
}

function siteSelect(site: MapReviewSiteRow): void {
	const toggling = selectedSiteID.value === site.id;
	selectedSiteID.value = toggling ? 0 : site.id;
	selectedSite.value = toggling ? undefined : site;
	if (!toggling) {
		paramSite.setValue(site.id.toString());
		flyToSite(site);
	} else {
		paramSite.removeValue();
	}
}

function flyToSite(site: MapReviewSiteRow): void {
	if (site.longitude == null || site.latitude == null) {
		return;
	}
	mapInstance.map?.flyTo({
		center: [site.longitude, site.latitude],
		zoom: 16,
		duration: 600,
	});
}

function onMapLoad(event: MglEvent<"load">) {
	const m = event.map;
	// Fit the district service area on first load.
	const bounds = boundsForServiceArea(session.organization?.service_area);
	if (bounds) {
		m.fitBounds(bounds, { padding: 40, duration: 800 });
	}
	// Click a site dot to select it.
	m.on("click", "review-site-dots", (e: MapLayerMouseEvent) => {
		const feature = e.features?.[0];
		if (!feature) {
			return;
		}
		const id = feature.properties?.id as number | undefined;
		if (id == null) {
			return;
		}
		const site = sites.value.find((s) => s.id === id);
		if (site) {
			siteSelect(site);
		}
	});
}

// A deep link (`?site=123`) selects its site from the loaded rows: the watcher
// covers a param that changes while the view is up, and the load re-runs the
// same selection, because the rows are not in hand yet when the view mounts.
function selectFromParam(site_id: string | null): void {
	const id = parseInt(site_id ?? "", 10);
	if (!id || selectedSiteID.value === id) {
		return;
	}
	const site = sites.value.find((s) => s.id === id);
	if (site) {
		siteSelect(site);
	}
}

// One search's answer, and the guard that keeps a slower earlier answer from
// overwriting a later one.
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let requestSeq = 0;

async function load(): Promise<void> {
	const seq = ++requestSeq;
	try {
		const page = await storeSite.query({ filter: searchFilter() });
		if (seq !== requestSeq) {
			return;
		}
		sites.value = page.items;
		collectionTotal.value = page.metadata.collection_total;
	} catch (err) {
		if (seq !== requestSeq) {
			return;
		}
		error.value = err instanceof Error ? err.message : "Unknown error";
	}
}

// Lifecycle
onMounted(async () => {
	await load();
	selectFromParam(paramSite.value.value);
});
watch(search, () => {
	clearTimeout(searchTimer);
	searchTimer = setTimeout(() => {
		void load();
	}, SEARCH_DEBOUNCE_MS);
});
watch(paramSite.value, selectFromParam, { immediate: true });
</script>
